import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { branchService } from '../services/api';
import { Card, Input, Button, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const CreateEditBranch = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [nameError, setNameError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Branch' : isEditMode ? 'Edit Branch' : 'Add Branch');
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchBranchData();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchBranchData = async () => {
    try {
      setLoading(true);
      const response = await branchService.getById(id);
      
      setCode(response.data.code);
      setName(response.data.name);
    } catch (err) {
      toast.error('Failed to fetch branch details');
      console.error(err);
      navigate('/admin/branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setCodeError('');
    setNameError('');
    setError('');
    
    // Validate
    let hasError = false;
    if (!code.trim()) {
      setCodeError('Branch code is required');
      hasError = true;
    } else if (code.length > 10) {
      setCodeError('Branch code must not exceed 10 characters');
      hasError = true;
    }

    if (!name.trim()) {
      setNameError('Branch name is required');
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoading(true);
      
      if (isEditMode) {
        await branchService.update(id, { code, name });
        toast.success('Branch updated successfully');
      } else {
        await branchService.create({ code, name });
        toast.success('Branch created successfully');
      }
      
      navigate('/admin/branches');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} branch`;
      setError(errorMsg);
      toast.error(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/branches');
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading branch details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: 'Branches', to: '/admin/branches' },
              { label: isViewMode ? 'View Branch' : isEditMode ? 'Edit Branch' : 'Add Branch' }
            ]}
            className="mb-6"
          />
          
          {/* Inputs Row: Code first, then Name */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            {/* Branch Code */}
            <div className="flex-1">
              <Input
                label="Branch Code"
                name="code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (codeError) setCodeError('');
                }}
                placeholder="Enter branch code"
                required
                error={codeError}
                maxLength={10}
                disabled={loading || isViewMode || isEditMode}
                readOnly={isViewMode || isEditMode}
              />
            </div>
            {/* Branch Name */}
            <div className="flex-1">
              <Input
                label="Branch Name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Enter branch name"
                required
                error={nameError}
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>
          
          {/* Actions Row: Buttons at far right */}
          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-primary-600 text-primary-600 hover:bg-primary-50"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Branch' : '+ Add Branch'}
              </Button>
            )}
            {isViewMode && (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/branches/edit/${id}`)}
              >
                Edit Branch
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditBranch;
